/**
 * TutorShellHeader — l'en-tête de l'espace tuteur, et sa feuille de profil.
 *
 * De la chrome d'espace, pas un écran. Le `<ParentHeader>` du DS habillé pour le masteur, comme la
 * maquette (`TutorSpace`, `pages-tuteur-espace--*`) : la marque qui ramène à l'accueil, le bouton
 * d'aide quand le masteur a le chat du support (l'ancienne bulle `HelpFAB`, devenue un bouton de
 * l'en-tête comme chez le parent), et sa pastille signée, avec un point tant que son e-mail n'est pas
 * vérifié (l'ancien `TutorMainMenuHeader`).
 *
 * La pastille ouvre sa feuille de profil, qui EST son compte (il n'y a plus de page « Mon compte ») :
 * lui en tête, « Membre depuis … », son fuseau s'il diffère de celui du serveur ; « Valide ton adresse
 * email » tant qu'elle ne l'est pas ; les portes que ses droits ouvrent (Ma dispo, Mes factures, Suivre
 * mes paiements, Historique des lives) ; « Changer ma photo » (le menu de l'avatar, par-dessus la
 * feuille : `useTutorAvatarState`) ; « Paramètres » ; « Apparence » (Clair par défaut) ; « Se
 * déconnecter » et la version de l'app.
 */
import Constants from "expo-constants"
import React, { createContext, useContext } from "react"
import { useSelector } from "react-redux"

import { ParentHeader, ProfileSwitcherSheet, Snackbar } from "../../../../../design-system"
import { AccountAvatarMenu, type ProfileSheetLink } from "../../../../../design-system/tutor"
import { AbilityContext } from "../../../../../data/utility/Can"
import { tutorShellViewModel } from "../../../view-models/tutor/TutorShell/tutorShellViewModel"
import { type UITutorAccountLinkKey } from "../../types/tutorCoquilleAccueil"
import { useIntercomAdapter } from "../../hooks/useIntercomAdapter"
import { setTutorAppearancePreference, useTutorAppearancePreference } from "../../hooks/tutorAppearancePreference"
import { useTutorAvatarState } from "./Account/useTutorAvatarState"
import { useTutorShellState } from "./useTutorShellState"

/** Les portes de l'ancien « Mon compte » du masteur, leur mot, leur glyphe, leur route. */
const ACCOUNT_LINKS: Record<UITutorAccountLinkKey, Omit<ProfileSheetLink, "key" | "onPress"> & { route?: string }> = {
    availability: { label: "Ma dispo", iconName: "calendar-alt", route: "tutor/ma-dispo" },
    invoices: { label: "Mes factures", iconName: "file-invoice", route: "tutoring-invoices" },
    // Le tableau de bord Stripe est une page web : son adresse se demande d'abord.
    payments: { label: "Suivre mes paiements", iconName: "euro-sign", external: true, keepOpen: true },
    livesHistory: { label: "Historique des lives", iconName: "chart-line", route: "tutor-lives-history" },
}

/**
 * L'ouverture de la messagerie du support (`useOpenIntercomChat`, descendue par la coquille legacy),
 * posée par `TutorNavigator` pour l'en-tête de chaque onglet. Absente : pas de bouton d'aide.
 */
export const TutorSupportContext = createContext<(() => void) | undefined>(undefined)

export function TutorShellHeader() {
    const ability = useContext(AbilityContext) as { can?: (action: string, subject: string) => boolean } | undefined
    const vm = useSelector((state: unknown) => tutorShellViewModel(state, !!ability?.can?.("read", "my_students")))
    const shell = useTutorShellState()
    const appearance = useTutorAppearancePreference()
    const support = useIntercomAdapter({ onOpen: useContext(TutorSupportContext) })
    const avatar = useTutorAvatarState()
    const sheetRefusal =
        avatar.error != null
            ? { message: avatar.error, dismiss: avatar.dismissError }
            : shell.paymentsError != null
              ? { message: shell.paymentsError, dismiss: shell.dismissPaymentsError }
              : null

    const links: ProfileSheetLink[] = [
        ...(vm.me.alert === true
            ? [
                  {
                      key: "validateEmail",
                      label: "Valide ton adresse email",
                      iconName: "envelope" as const,
                      onPress: () => shell.push("validate-email"),
                  },
              ]
            : []),
        ...vm.accountLinks.map((key) => {
            const { route, ...link } = ACCOUNT_LINKS[key]
            return {
                key,
                ...link,
                loading: key === "payments" && shell.paymentsLoading,
                onPress: route != null ? () => shell.push(route) : shell.openPayments,
            }
        }),
        // Le menu de l'avatar s'ouvre par-dessus la feuille, qui reste ouverte et tourne pendant l'envoi.
        {
            key: "avatar",
            label: "Changer ma photo",
            iconName: "camera",
            keepOpen: true,
            loading: avatar.uploading,
            onPress: avatar.openMenu,
        },
        { key: "settings", label: "Paramètres", iconName: "cog", onPress: () => shell.push("settings") },
    ]

    return (
        <>
            <ParentHeader
                parentFirstname={vm.me.firstname}
                onSwitchProfile={shell.openSheet}
                profile={{
                    legend: "",
                    avatarUri: vm.me.avatarUri,
                    alert: vm.me.alert === true,
                    chevron: false,
                    signed: true,
                    accessibilityLabel: "Mon espace",
                }}
                onContactSupport={support.onContactSupport}
                supportUnreadCount={support.unreadCount}
                onPressLogo={shell.openHome}
            />
            <ProfileSwitcherSheet
                visible={shell.sheetOpen}
                onClose={shell.closeSheet}
                me={{
                    name: vm.me.displayName,
                    avatarUri: vm.me.avatarUri,
                    title: vm.me.memberSince != null ? `Membre depuis ${vm.me.memberSince}` : undefined,
                    notes: vm.me.timezone != null ? [`Fuseau horaire actuel : ${vm.me.timezone}`] : undefined,
                }}
                meAvatarVariant="tutor"
                links={links}
                appearance={{ value: appearance, onChange: setTutorAppearancePreference }}
                onSwitchAccount={shell.logout}
                switchingAccount={shell.loggingOut}
                version={Constants.expoConfig?.version}
                parentActive={false}
            />
            <AccountAvatarMenu {...avatar.menu} />
            {/* Un refus de la feuille : la photo, ou « Suivre mes paiements ». */}
            <Snackbar
                visible={sheetRefusal != null}
                message={sheetRefusal?.message ?? ""}
                tone="danger"
                onDismiss={sheetRefusal?.dismiss ?? shell.dismissPaymentsError}
            />
        </>
    )
}
